import { FunctionComponent, SVGProps } from "react";

/*
 * The interface used to draw its icons with characters — ✕ for the modal, × for the
 * filter, ☆/★ for a rating. A glyph is whatever the font on the device decides it is:
 * the two crosses came out different weights on macOS and the same weight on Android,
 * and the stars are emoji-presentation on iOS, so a rating rendered in colour there.
 *
 * These are drawn instead. Sized in `em` and stroked in `currentColor`, so an icon
 * inherits the size and colour of the text it sits beside rather than needing to be
 * re-tuned for every button it appears in.
 */

export type IconProps = Omit<SVGProps<SVGSVGElement>, "children"> & {
  /** Overrides the default `1em`, for the few places an icon is not beside text. */
  size?: string | number;
};

const Icon: FunctionComponent<IconProps & { children: React.ReactNode }> = ({
  size = "1em",
  children,
  ...rest
}) => (
  <svg
    viewBox="0 0 20 20"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    // Decorative by default: every icon here sits in a control that carries its own
    // accessible name, so announcing the icon too would say it twice.
    aria-hidden="true"
    focusable="false"
    {...rest}
  >
    {children}
  </svg>
);

export const SearchIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <circle cx="8.75" cy="8.75" r="5.25" />
    <path d="m12.6 12.6 3.9 3.9" />
  </Icon>
);

export const CloseIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <path d="m5.5 5.5 9 9M14.5 5.5l-9 9" />
  </Icon>
);

export const PlusIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <path d="M10 4.5v11M4.5 10h11" />
  </Icon>
);

export const TrashIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <path d="M3.75 5.5h12.5M8 5.5V4.25A.75.75 0 0 1 8.75 3.5h2.5a.75.75 0 0 1 .75.75V5.5" />
    <path d="M5.5 5.5v9.75a1.25 1.25 0 0 0 1.25 1.25h6.5a1.25 1.25 0 0 0 1.25-1.25V5.5" />
    <path d="M8.5 8.75v4.5M11.5 8.75v4.5" />
  </Icon>
);

export const ChevronDownIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <path d="m5.5 8 4.5 4.5L14.5 8" />
  </Icon>
);

export const ArrowLeftIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <path d="M16 10H4.5M9 4.5 3.5 10 9 15.5" />
  </Icon>
);

export const ExternalLinkIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <path d="M11 4h5v5M16 4l-7 7" />
    <path d="M15 12v3.25A1.75 1.75 0 0 1 13.25 17h-8.5A1.75 1.75 0 0 1 3 15.25v-8.5A1.75 1.75 0 0 1 4.75 5H8" />
  </Icon>
);

export const SignInIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <path d="M8.5 3.5H5.25A1.75 1.75 0 0 0 3.5 5.25v9.5A1.75 1.75 0 0 0 5.25 16.5H8.5" />
    <path d="M12.5 6.75 15.75 10l-3.25 3.25M15.25 10H7.5" />
  </Icon>
);

/**
 * A star, hollow or solid.
 *
 * One path filled or not, rather than two shapes, so the outline of a set star sits in
 * exactly the same place as the outline of an unset one — otherwise the row of five
 * shifts by a fraction of a pixel as it fills.
 */
export const StarIcon: FunctionComponent<IconProps & { filled?: boolean }> = ({
  filled = false,
  ...props
}) => (
  <Icon fill={filled ? "currentColor" : "none"} {...props}>
    <path d="M10 3.25l2.06 4.18 4.61.67-3.34 3.25.79 4.59L10 13.77l-4.12 2.17.79-4.59L3.33 8.1l4.61-.67z" />
  </Icon>
);

/**
 * A pencil, as a solid shape rather than an outline.
 *
 * The stroked version read as the heaviest thing on the row — a 1.5px outline at 16px
 * is nearly all edge, so it came out darker and sharper than the hollow stars beside
 * it even in the same colour. A filled silhouette carries its weight in area instead,
 * which is what lets opacity soften it without it turning into a smudge.
 */
export const PencilIcon: FunctionComponent<IconProps> = (props) => (
  <Icon fill="currentColor" stroke="none" {...props}>
    <path d="M13.586 3.586a2 2 0 1 1 2.828 2.828l-.793.793-2.828-2.828.793-.793z" />
    <path d="M11.379 5.793 3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
  </Icon>
);
